import React from "react";
import { motion } from "framer-motion";
import { BtnSignature } from "./BtnSignature";
import { StatusChip } from "./StatusChip";
import { WorkflowBar13 } from "./WorkflowBar13";
import { SilhouetteVehicle } from "./SilhouetteVehicle";

/**
 * DossierCard V2 — "fiche produit" façon Apple/Tesla.
 * Photo XL à gauche, colonne info épurée, action ghost à droite.
 * Aucune bordure interne — tout se joue sur l'espace et la subtilité.
 * Sous la carte, sur toute la largeur, une barre 13 ultra fine.
 */
export function DossierCard({
  dossier,
  onAction,
  className = "",
  testid,
  index = 0,
}) {
  const {
    client,
    vehicle,
    plate,
    status = "encours",
    action,
    urgent = false,
    image,
    completed = 5,
    silhouette = "berline",
  } = dossier;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.06 * index,
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -2 }}
      data-testid={testid}
      className={`shadow-wyvio-elev group relative overflow-hidden rounded-[24px] bg-white transition-shadow duration-300 hover:shadow-wyvio-elev-hover ${className}`}
    >
      {urgent && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-[2px]"
          style={{ background: "#C8FF2F" }}
        />
      )}

      <div className="flex items-stretch gap-8 p-6">
        {/* Photo véhicule XL — IA sélectionne la meilleure photo extérieure
            du dossier. Silhouette premium en fallback. */}
        <div className="relative h-[168px] w-[288px] flex-shrink-0 overflow-hidden rounded-[16px]">
          {image ? (
            <img
              src={image}
              alt={vehicle}
              className="h-full w-full object-cover transition-transform duration-[600ms] group-hover:scale-[1.025]"
            />
          ) : (
            <SilhouetteVehicle
              type={silhouette}
              hint={dossier.photosPending}
              className="h-full w-full"
            />
          )}
          <div
            aria-hidden
            className="absolute inset-0 rounded-[16px]"
            style={{ boxShadow: "inset 0 0 0 1px rgba(15,15,15,0.05)" }}
          />
        </div>

        {/* Info */}
          <div className="flex min-w-0 flex-1 flex-col justify-between py-2">
          <div>
            <div className="flex items-center gap-3">
              <StatusChip status={status} />
              {plate && (
                <span className="wyvio-tabular text-[10.5px] font-medium uppercase tracking-[0.16em] text-[#8A8A80]">
                  {plate}
                </span>
              )}
            </div>
            <h3 className="mt-5 truncate text-[26px] font-medium leading-[1.05] tracking-[-0.022em] text-[#151515]">
              {vehicle}
            </h3>
            <p className="mt-2 truncate text-[13.5px] font-normal text-[#6B6B67]">
              {client}
            </p>
          </div>

          <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.2em] text-[#8A8A80]">
            Étape {completed} / 13
          </p>
        </div>

        {/* Action à droite */}
        <div className="flex flex-col items-end justify-center py-2">
          <BtnSignature
            variant={urgent ? "lime" : "primary"}
            arrow
            onClick={onAction}
            testid={`${testid}-cta`}
          >
            {action}
          </BtnSignature>
        </div>
      </div>

      {/* Workflow bar en pied de carte, ultra fine, monochrome */}
      <div className="px-6 pb-5">
        <WorkflowBar13 completed={completed} compact variant="soft" />
      </div>
    </motion.div>
  );
}
